import React, { useState } from "react";
import { GenericTableColumn } from "@ui-components/Table/GenericTable";
import GenericDropdown from "@ui-components/Dropdown/GenericDropdown";

export interface Auth0User {
  user_id: string;
  name: string;
  email: string;
  hasMFA: boolean;
  status: string;
  roles: Array<{
    id: number;
    name: string;
  }>;
}

interface ActionsDropdownProps {
  row: Auth0User;
  onEdit: (userId: string) => void;
  onDelete: (userId: string) => void;
  onBlock: (userId: string, isBlocked: boolean) => void;
  onResetPassword: (email: string) => void;
  onResetMFA: (userId: string) => void;
  ssoActive: boolean;
}

export function ActionsDropdown({
  row,
  onEdit,
  onDelete,
  onBlock,
  onResetPassword,
  onResetMFA,
  ssoActive,
}: ActionsDropdownProps) {
  const [dropdownKey, setDropdownKey] = useState(0);
  const actions =
    row.status === "blocked"
      ? ["Edit", "Delete", "Unblock"]
      : ["Edit", "Delete", "Block"];
  if (row.user_id.startsWith("auth0|")) {
    actions.push("Reset Password");
  }
    
  if (!ssoActive) {
    actions.push("Reset MFA");
  }

  const handleSelect = (action: string) => {
    switch (action) {
      case "Edit":
        onEdit(row.user_id);
        break;
      case "Delete":
        onDelete(row.user_id);
        break;
      case "Block":
        onBlock(row.user_id, false);
        break;
      case "Unblock":
        onBlock(row.user_id, true);
        break;
      case "Reset Password":
        onResetPassword(row.email);
        break;
      case "Reset MFA":
        onResetMFA(row.user_id);
        break;
    }
    setDropdownKey((prev) => prev + 1);
  };

  return (
    <GenericDropdown
      key={dropdownKey}
      items={actions}
      onSelect={handleSelect}
      defaultLabel="Select an option"
      widthClass="w-[150px]"
      maxWidthClass="max-w-[150px]"
      className="text-sm text-semantic-text-primary"
      size="sm"
      fixedPosition={true}
    />
  );
}

export const auth0UsersColumns: GenericTableColumn<Auth0User>[] = [
  {
    header: "Full Name",
    renderCell: (row) => row.name,
  },
  {
    header: "Email",
    renderCell: (row) => row.email,
  },
  {
    header: "MFA",
    renderCell: (row) => {
      if (row.hasMFA) {
        return (
          <span className="px-2 py-1 rounded text-xs font-semibold bg-green-100 text-green-600">
            Configured
          </span>
        );
      } else {
        return (
          <span className="px-2 py-1 rounded text-xs font-semibold bg-red-100 text-red-600">
            Not Configured
          </span>
        );
      }
    },
  },
  {
    header: "Status",
    renderCell: (row) => {
      const originalStatus = row.status;
      const lowerStatus = row.status.toLowerCase();
      let classes = "bg-gray-100 text-gray-600";

      if (lowerStatus === "active") {
        classes = "bg-green-100 text-green-600";
      } else if (lowerStatus === "blocked") {
        classes = "bg-orange-100 text-orange-600";
      }

      const capitalizedStatus =
        originalStatus.charAt(0).toUpperCase() + originalStatus.slice(1).toLowerCase();

      return (
        <span className={`px-2 py-1 rounded text-xs font-semibold ${classes}`}>
          {capitalizedStatus}
        </span>
      );
    },
  },
  {
    header: "Role",
    renderCell: (row) => row.roles.map((r) => r.name).join(", "),
  },
  {
    header: "Actions",
  },
];
